import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  signal,
  viewChild,
} from '@angular/core';

import { ECharts, init } from 'echarts';

import type { AggregatedEvaluationsQuestionOption } from '@core/evaluations-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { Theme } from '@core/theme-util';
import { EmptyStateComponent, HeadingComponent } from '@core/user-interface-legacy';

import { OptionsDistributionTooltipComponent } from './options-distribution-tooltip/options-distribution-tooltip.component';
import { createChartTooltip } from '../../chart-tooltip/create-chart-tooltip';

const MAX_COLUMN_OPTIONS = 5;
const BAR_WIDTH = 30;
const BAR_MIN_HEIGHT = 4;
const BAR_BORDER_RADIUS = 4;
const COLUMN_MAX_WIDTH = 100;
const ROW_HEIGHT = 44;
const VALUE_AXIS_HEIGHT = 24;
const VALUE_AXIS_WIDTH = 48;
const COLUMN_BAR_MAX_SLOT_RATIO = 0.6;
const COLUMN_CHART_HEIGHT = 190;
const CATEGORY_LABEL_MAX_WIDTH = 120;
const CATEGORY_LABEL_MARGIN = 8;
const LABEL_FONT_SIZE = 14;

@Component({
  selector: 'rt-candidate-offer-evaluation-question-options-distribution',
  templateUrl: './candidate-offer-evaluation-question-options-distribution.component.html',
  styleUrl: './candidate-offer-evaluation-question-options-distribution.component.less',
  imports: [EmptyStateComponent, HeadingComponent, TranslatePipe],
})
export class CandidateOfferEvaluationQuestionOptionsDistributionComponent {
  private readonly destroyRef = inject(DestroyRef);

  private readonly chartTooltip = createChartTooltip({
    injector: inject(Injector),
    component: OptionsDistributionTooltipComponent,
    placement: () => (this.hasHorizontalBars() ? 'right' : 'top'),
    inputs: params => {
      const param = Array.isArray(params) ? params[0] : params;
      return {
        title: this.questionTitle(),
        label: <string>param.name,
        value: <number>param.value,
        color: param.color ? String(param.color) : this.theme().baseBrand400,
      };
    },
  });

  public questionTitle = input.required<string | null>();
  public answersCount = input.required<number>();
  public commentsCount = input(0);
  public options = input.required<AggregatedEvaluationsQuestionOption[]>();
  public theme = input.required<Theme>();

  protected container = viewChild.required<ElementRef<HTMLDivElement>>('chartContainer');

  protected hasHorizontalBars = computed(() => this.options().length > MAX_COLUMN_OPTIONS);

  protected chartHeight = computed(() =>
    this.hasHorizontalBars()
      ? this.options().length * ROW_HEIGHT + VALUE_AXIS_HEIGHT
      : COLUMN_CHART_HEIGHT,
  );

  protected chartOptions = computed(() => {
    const theme = this.theme();
    const hasHorizontalBars = this.hasHorizontalBars();

    const categoryAxis = {
      type: 'category',
      data: this.options().map(option => option.title),
      axisLabel: {
        interval: 0,
        color: theme.colorTextStrongDefault,
        fontSize: LABEL_FONT_SIZE,
        width: this.categoryLabelWidth(),
        overflow: 'truncate',
      },
      axisLine: { show: false },
      axisTick: { show: false },
    };
    const valueAxis = {
      type: 'value',
      minInterval: 1,
      max: Math.max(...this.options().map(option => option.count), 0) + 1,
      axisLabel: { color: theme.colorTextDefault, fontSize: LABEL_FONT_SIZE },
      splitLine: { lineStyle: { color: theme.colorBorderLowDefault } },
    };

    return {
      animation: false,
      tooltip: this.chartTooltip,
      grid: hasHorizontalBars
        ? {
            left: CATEGORY_LABEL_MAX_WIDTH + CATEGORY_LABEL_MARGIN,
            right: 16,
            top: 8,
            bottom: VALUE_AXIS_HEIGHT,
            containLabel: false,
          }
        : { left: 0, right: 16, top: 8, bottom: 0, containLabel: true },
      xAxis: hasHorizontalBars ? valueAxis : categoryAxis,
      yAxis: hasHorizontalBars ? { ...categoryAxis, inverse: true } : valueAxis,
      series: [
        {
          type: 'bar',
          data: this.options().map(option =>
            option.count
              ? { value: option.count }
              : {
                  value: 0,
                  itemStyle: { color: theme.colorBgNeutralStrongDefault },
                  label: { color: theme.colorTextLowDefault },
                },
          ),
          barMinHeight: BAR_MIN_HEIGHT,
          ...(hasHorizontalBars
            ? { barWidth: BAR_WIDTH }
            : { barMaxWidth: this.columnBarMaxWidth() }),
          itemStyle: {
            color: theme.baseBrand400,
            borderRadius: hasHorizontalBars
              ? [0, BAR_BORDER_RADIUS, BAR_BORDER_RADIUS, 0]
              : [BAR_BORDER_RADIUS, BAR_BORDER_RADIUS, 0, 0],
          },
          label: {
            show: true,
            position: hasHorizontalBars ? 'right' : 'top',
            fontSize: LABEL_FONT_SIZE,
            color: theme.colorTextStrongDefault,
          },
        },
      ],
    };
  });

  private containerWidth = signal(0);

  private columnSlotWidth = computed(() => {
    const containerWidth = this.containerWidth();

    return containerWidth
      ? Math.floor((containerWidth - VALUE_AXIS_WIDTH) / this.options().length)
      : 0;
  });

  private categoryLabelWidth = computed(() => {
    const columnSlotWidth = this.columnSlotWidth();

    return this.hasHorizontalBars() || !columnSlotWidth
      ? CATEGORY_LABEL_MAX_WIDTH
      : Math.min(CATEGORY_LABEL_MAX_WIDTH, Math.max(0, columnSlotWidth - CATEGORY_LABEL_MARGIN));
  });

  private columnBarMaxWidth = computed(() => {
    const columnSlotWidth = this.columnSlotWidth();
    const maxWidth = Math.floor((COLUMN_MAX_WIDTH * MAX_COLUMN_OPTIONS) / this.options().length);

    return columnSlotWidth
      ? Math.min(maxWidth, Math.floor(columnSlotWidth * COLUMN_BAR_MAX_SLOT_RATIO))
      : maxWidth;
  });

  private chart?: ECharts;
  private _updateChart = effect(() => {
    const chartOptions = this.chartOptions();
    if (chartOptions && this.chart) {
      this.chart.setOption(chartOptions);
    }
  });

  protected ngAfterViewInit() {
    if (this.answersCount() === 0) {
      return;
    }

    const element = this.container().nativeElement;

    const resizeObserver = new ResizeObserver(() => {
      if (!element.clientWidth || !element.clientHeight) {
        return;
      }

      this.containerWidth.set(element.clientWidth);

      if (this.chart) {
        this.chart.resize();
      } else {
        this.chart = init(element, undefined, { renderer: 'svg' });
        this.chart.setOption(this.chartOptions());
      }
    });
    resizeObserver.observe(element);

    this.destroyRef.onDestroy(() => {
      resizeObserver.disconnect();
      this.chart?.dispose();
    });
  }
}
